import { Modal } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { HttpStatusCode } from '@/fetch/core/constant';
import {
  VehicleStatus,
  vehicleStatusApi,
} from '@/fetch/bussiness/vehicleStatus';
import { reportSgmEvent, SgmEventType } from '@/utils/sgm';

const MONITOR_TAKEOVER_SOURCE = 'MONITOR';

type AccessState =
  | { status: 'checking' }
  | { status: 'allowed' }
  | { status: 'denied'; requestKey: string; message: string };

const isCurrentMonitorTakeover = (
  vehicleStatus: VehicleStatus,
  userName: string,
) =>
  vehicleStatus.takeOverUserName === userName &&
  vehicleStatus.takeOverSource === MONITOR_TAKEOVER_SOURCE;

const hasTakeoverIdentity = (vehicleStatus: VehicleStatus) =>
  typeof vehicleStatus.takeOverUserName === 'string' &&
  typeof vehicleStatus.takeOverSource === 'string';

/**
 * Guards pages that can only be used by the current MONITOR takeover user.
 * URL parameters supply the target vehicle only; the user identity always
 * comes from the authenticated Redux state passed by the caller.
 */
export const useVehicleTakeoverAccess = (
  vehicleName: string | undefined,
  userName: string | undefined,
  page: 'remote_control' | 'location_reset',
) => {
  const [accessState, setAccessState] = useState<AccessState>({
    status: 'checking',
  });
  const requestRef = useRef<{
    key: string;
    promise: ReturnType<typeof vehicleStatusApi.getVehicleStatus>;
  } | null>(null);
  const shownModalKeyRef = useRef<string | null>(null);
  const reportedRequestKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    const reportAccessCheck = (
      requestKey: string,
      type: SgmEventType,
      result: 'allowed' | 'denied' | 'failed',
      reason: string,
      startedAt: number,
    ) => {
      if (reportedRequestKeyRef.current === requestKey) {
        return;
      }

      reportedRequestKeyRef.current = requestKey;
      reportSgmEvent({
        type,
        code: 'monitor_takeover_access_check',
        msg: {
          page,
          vehicleName: vehicleName || '',
          action: 'takeover_access_check',
          trigger: 'page_enter',
          requestId: null,
          result,
          reason,
        },
        cost1: Date.now() - startedAt,
      });
    };

    if (!userName) {
      setAccessState({ status: 'checking' });
      return () => {
        active = false;
      };
    }

    if (!vehicleName) {
      const requestKey = `invalid-vehicle:${userName}`;
      reportAccessCheck(
        requestKey,
        SgmEventType.ERROR,
        'failed',
        'invalid_vehicle_name',
        Date.now(),
      );
      setAccessState({
        status: 'denied',
        requestKey,
        message: '车辆状态校验失败，无法进入该页面。',
      });
      return () => {
        active = false;
      };
    }

    const requestKey = `${vehicleName}:${userName}`;
    const startedAt = Date.now();
    setAccessState({ status: 'checking' });

    const requestPromise =
      requestRef.current?.key === requestKey
        ? requestRef.current.promise
        : vehicleStatusApi.getVehicleStatus({ vehicleName });
    requestRef.current = {
      key: requestKey,
      promise: requestPromise,
    };

    requestPromise
      .then((response) => {
        if (!active) {
          return;
        }

        if (response?.code !== HttpStatusCode.Success || !response.data) {
          reportAccessCheck(
            requestKey,
            SgmEventType.ERROR,
            'failed',
            'status_response_invalid',
            startedAt,
          );
          setAccessState({
            status: 'denied',
            requestKey,
            message: '车辆状态校验失败，无法进入该页面。',
          });
          return;
        }

        if (!hasTakeoverIdentity(response.data)) {
          reportAccessCheck(
            requestKey,
            SgmEventType.ERROR,
            'failed',
            'takeover_identity_missing',
            startedAt,
          );
          setAccessState({
            status: 'denied',
            requestKey,
            message: '车辆状态校验失败，无法进入该页面。',
          });
          return;
        }

        if (!isCurrentMonitorTakeover(response.data, userName)) {
          const reason =
            response.data.takeOverUserName !== userName
              ? 'takeover_user_mismatch'
              : 'takeover_source_not_monitor';
          reportAccessCheck(requestKey, SgmEventType.WARN, 'denied', reason, startedAt);
          setAccessState({
            status: 'denied',
            requestKey,
            message: '当前车辆未由你在监控系统接管，无法进入该页面。',
          });
          return;
        }

        reportAccessCheck(
          requestKey,
          SgmEventType.INFO,
          'allowed',
          'current_monitor_takeover',
          startedAt,
        );
        setAccessState({ status: 'allowed' });
      })
      .catch(() => {
        if (!active) {
          return;
        }

        reportAccessCheck(
          requestKey,
          SgmEventType.ERROR,
          'failed',
          'status_request_error',
          startedAt,
        );
        setAccessState({
          status: 'denied',
          requestKey,
          message: '车辆状态校验失败，无法进入该页面。',
        });
      });

    return () => {
      active = false;
    };
  }, [page, userName, vehicleName]);

  useEffect(() => {
    if (
      accessState.status !== 'denied' ||
      shownModalKeyRef.current === accessState.requestKey
    ) {
      return;
    }

    shownModalKeyRef.current = accessState.requestKey;
    Modal.error({
      title: accessState.message,
      className: 'error-page-modal',
      maskClosable: false,
      onOk: () => {
        window.history.back();
      },
    });
  }, [accessState]);

  return accessState.status === 'allowed';
};
